import React, { useMemo, useState } from 'react';

import {
  dashboardCustomsRequestApis,
  dashboardCustomsRequestURLSubmitApis,
} from '@apis/next/customsRequestApis';
import { dashboardCustomsRequestLogApis } from '@apis/next/customsRequestLogApis';
import { dashboardPartnerApis } from '@apis/next/partnerApis';
import copySvg from '@assets/img/icons/copy.svg';
import { CustomsRequestSubmissionTargetInput } from '@components/admin/booking/CustomsRequestSubmissionTargetInput';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogActions,
  ActionableDialogContent,
  ActionableDialogTitle,
} from '@components/Dialog';
import { IndexedFormContainer, IndexedLabel } from '@components/NextForms/IndexedForm';
import { allCustomsRequestSubmissionInternalTargets } from '@data/customsRequest';
import { useFormUtils } from '@hooks/useFormUtils';
import {
  CustomsRequestHelper,
  type CustomsRequest,
  type CustomsRequestURLSubmitResult,
  CustomsRequestSubmitForm,
} from '@models/next/CustomsRequest';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { useQueryClient } from '@tanstack/react-query';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import copy from 'clipboard-copy';
import clsx from 'clsx';

const strings = createStrings({
  ja: {
    title: '通関資料リンクを作成する',
    cannotURLSubmitCustomsRequest: `下記の原因で処理できません：`,
    customsRequestURLsCreatedPrompt: '配車資料のURLが作成しました：',
    copySuccessMessage: 'リンクをコピーしました。',
    create: '作成',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  dialogContent: {
    ...common.flexColumn,
    rowGap: '1rem',
    padding: '2.5rem 2rem',
    flexShrink: 1,
    minHeight: 0,
    maxHeight: 550,
  },
}));

export interface CustomsRequestURLSubmitDialogProps {
  customsRequest: CustomsRequest;
  open: boolean;
  onClose: () => void;
}

const CustomsRequestURLSubmitDialogContent: React.FC<
  CustomsRequestURLSubmitDialogProps & {
    onSubmitSuccess: (v: CustomsRequestURLSubmitResult) => void;
  }
> = ({ customsRequest, onClose, onSubmitSuccess }) => {
  const { d2d_no } = customsRequest;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const queryClient = useQueryClient();

  const partner = dashboardPartnerApis.useGet('/dashboard/partner');

  const submissionTargets = useMemo(
    () =>
      partner.data
        ? [
            ...allCustomsRequestSubmissionInternalTargets,
            ...partner.data.map(CustomsRequestHelper.mapPartnerToSubmissionTarget),
          ]
        : allCustomsRequestSubmissionInternalTargets,
    [partner.data, allCustomsRequestSubmissionInternalTargets],
  );

  const customsRequestURLSubmit = dashboardCustomsRequestURLSubmitApis.usePut(
    '/dashboard/customs-request/:d2d_no',
    {
      queries: {
        action: 'URL_SUBMIT',
      },
      params: {
        d2d_no,
      },
    },
    {
      onSuccess() {
        queryClient.invalidateQueries(
          dashboardCustomsRequestLogApis.getKeyByPath(
            'get',
            '/dashboard/customs-request-log/:d2d_no',
          ),
        );
        queryClient.invalidateQueries(
          dashboardCustomsRequestApis.getKeyByPath('get', '/dashboard/customs-request/:d2d_no'),
        );
      },
    },
  );

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [customsRequestURLSubmit],
  });

  const form = useForm(CustomsRequestSubmitForm, {
    initialOutput: {
      company: customsRequest.submission_target?.company ?? '',
    },
  });

  const { fields } = form.root;

  return (
    <>
      <form
        {...form.bindForm({
          async onSubmit(values) {
            const submission_target = submissionTargets.find((t) => t.company === values.company);

            if (!submission_target) {
              throw new Error('Unexpected invalid submission target');
            }

            onSubmitSuccess(await customsRequestURLSubmit.mutateAsync({ submission_target }));
          },
        })}
      >
        <div className={classes.dialogContent}>
          <div>{renderAlertsAndSpinnerModal()}</div>

          <IndexedFormContainer size="lg">
            <IndexedLabel field={fields.company} />
            <div>
              <CustomsRequestSubmissionTargetInput
                field={fields.company}
                submissionTargets={submissionTargets}
              />
            </div>
          </IndexedFormContainer>
        </div>

        <ActionableDialogActions justifyContent="center">
          <Button
            color="primary"
            size="small"
            text={formStrings.prev}
            type="button"
            shape="round"
            onClick={onClose}
            style={{ width: 100 }}
          />
          <Button
            color="primary"
            size="small"
            text={strings.create}
            style={{ width: 100 }}
            shape="round"
          />
        </ActionableDialogActions>
      </form>
    </>
  );
};

export const CustomsRequestURLSubmitDialog: React.FC<CustomsRequestURLSubmitDialogProps> = (
  props,
) => {
  const { customsRequest, open, onClose } = props;

  const [step, setStep] = useState<'INPUT' | 'SUBMIT_RESULT'>('INPUT');

  const [copySuccess, setCopySuccess] = useState(false);

  const [submitResult, setSubmitResult] = useState<CustomsRequestURLSubmitResult | undefined>();

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const canSubmit = useMemo(
    () => new CustomsRequestHelper(customsRequest).canSubmit(),
    [customsRequest],
  );

  const renderErrors = () => {
    if (canSubmit._tag === 'Left') {
      return (
        <>
          <div
            className={clsx(classes.middleText, classes.textGray500)}
            style={{ padding: '2rem' }}
          >
            {strings.cannotURLSubmitCustomsRequest}

            <ul>
              {canSubmit.left.messages.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>

            <Button
              color="primary"
              style={{ width: 100, marginTop: 50 }}
              text="OK"
              size="small"
              shape="round"
              onClick={onClose}
            />
          </div>
        </>
      );
    }
  };

  return (
    <ActionableDialog open={open} onClose={onClose} size="lg">
      <ActionableDialogTitle>{strings.title}</ActionableDialogTitle>

      {canSubmit._tag === 'Right' && (
        <>
          {step === 'INPUT' && (
            <CustomsRequestURLSubmitDialogContent
              {...props}
              onSubmitSuccess={(v) => {
                setSubmitResult(v);
                setStep('SUBMIT_RESULT');
              }}
            />
          )}

          {step === 'SUBMIT_RESULT' && (
            <>
              <ActionableDialogContent>
                <div
                  className={clsx(classes.textGray700, classes.middleText)}
                  style={{ paddingTop: 20, paddingBottom: 20 }}
                >
                  <Alert open={copySuccess} severity="success" gutter>
                    {strings.copySuccessMessage}
                  </Alert>

                  <div>{strings.customsRequestURLsCreatedPrompt}</div>

                  <div>
                    <span className={classes.wordBreakBreakAll}>{submitResult?.url}</span>

                    <button
                      onClick={() => {
                        if (submitResult) {
                          copy(submitResult.url);
                          setCopySuccess(true);
                        }
                      }}
                      style={{ paddingLeft: 10, verticalAlign: 'sub' }}
                    >
                      <img src={copySvg} />
                    </button>
                  </div>
                </div>
              </ActionableDialogContent>

              <ActionableDialogActions justifyContent="center">
                <Button
                  type="submit"
                  color="primary"
                  size="small"
                  shape="round"
                  text={formStrings.prev}
                  style={{ width: 100 }}
                  onClick={onClose}
                />
              </ActionableDialogActions>
            </>
          )}
        </>
      )}

      {renderErrors()}
    </ActionableDialog>
  );
};
